import { Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon, EyeOffIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { MouseEvent, useCallback } from 'react';
import styled, { css } from 'styled-components';

import EditorNavItem from 'dashboard/apps/InquiryTemplateEditor/EditorNavItem';
import { ScreenEditorComponentConfigTypes } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import {
  componentTotalNumConditionalConfigsWithRulesLogic,
  isHiddenComponentConfig,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import { setComponentPanelSetActiveTab } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/hooks/useComponentEditorComponentPanelSettings';
import {
  SelectedComponentAndStep,
  SelectedComponentTab,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { InquiryTemplateVersionStepUi } from 'dashboard/models/inquiry-template-version';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { isActive, isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { isUiParentComponentConfig, UiComponentConfig } from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';

const iconPadding = css`
  padding: ${Spacing[1]};
`;

const IconWrapper = styled.div`
  ${iconPadding}
`;

const ComponentIconContainer = styled.div`
  padding: ${iconPadding};
`;

const ComponentLogicIconContainer = styled(ComponentIconContainer)`
  padding-left: ${Spacing[2]};
`;

const CenteredContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledEditorNavItem = styled(EditorNavItem)`
  ${IconWrapper} {
    display: none;
  }

  &${isHovered}${isNotDisabled},
    &${isFocused}${isNotDisabled},
    &${isHovered}${isFocused}${isNotDisabled},
    &${isActive} {
    ${IconWrapper} {
      display: block;
    }
  }
`;

const ChildComponentsContainer = styled.ul`
  padding-left: ${Spacing[4]};
`;

function stopClickEventBubbling(e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
}

type ScreenComponentListProps = {
  selectedStepConfig: InquiryTemplateVersionStepUi;
  selectedComponent?: string;
  setSelectedComponentAndStep: (value: SelectedComponentAndStep) => void;
  handleComponentDelete: (componentToDelete?: SelectedComponentAndStep) => void;
};

const ScreenComponentList = ({
  selectedStepConfig,
  selectedComponent,
  setSelectedComponentAndStep,
  handleComponentDelete,
}: ScreenComponentListProps) => {
  const { name, config } = selectedStepConfig;
  const { components } = config;

  const onLogicAttributeTriggerClick = useCallback(
    (e: MouseEvent<HTMLDivElement>, component: UiComponentConfig) => {
      stopClickEventBubbling(e);

      setComponentPanelSetActiveTab({
        componentPanelIsOpen: selectedComponent != null,
        componentName: component.name,
        stepName: selectedStepConfig.name,
        tab: SelectedComponentTab.LOGIC,
      });

      if (selectedComponent !== component.name) {
        setSelectedComponentAndStep({
          step: selectedStepConfig,
          component,
          error: false,
        });
      }
    },
    [selectedStepConfig, selectedComponent],
  );

  const buildComponentItems = useCallback(
    (stepName: string, components: UiComponentConfig[]) => {
      return components.map((component, idx) => {
        const componentItem = ScreenEditorComponentConfigTypes[component.type];
        const componentSupportedInEditor = componentItem != null;
        const isActive = selectedComponent === component.name;

        // (Note) this means we havent added the component to the list in ScreenEditorComponentConfigTypes
        if (!componentSupportedInEditor) {
          captureMessage(`Unknown UI component in list`, 'warning', {
            component: (component as any).type,
          });
        }

        const { LogicIcon, totalCount: totalCountofRulesLogicAttrs } =
          componentTotalNumConditionalConfigsWithRulesLogic(component);

        const onDeleteComponentButtonClick = (e: MouseEvent<HTMLDivElement>) => {
          stopClickEventBubbling(e);
          handleComponentDelete({
            step: selectedStepConfig,
            component,
            error: false,
          });
          // remove focus on re-render
          (document.activeElement as HTMLElement).blur();
        };

        // TODO: (Evan) - In future iteration, make list items draggable
        // via `react-beautiful-dnd`
        return (
          <div key={`${component.name}-${idx}`}>
            <StyledEditorNavItem
              id={`${component.name}-${component.type}`}
              active={isActive}
              onClick={() => {
                if (selectedComponent !== component.name) {
                  setSelectedComponentAndStep({
                    step: selectedStepConfig,
                    component,
                    error: false,
                  });
                }
              }}
              suffixIcon={
                <CenteredContainer>
                  {isHiddenComponentConfig(component) && (
                    <Tooltip
                      side='top'
                      interactive
                      iconTrigger={
                        <ComponentIconContainer>
                          <EyeOffIcon />
                        </ComponentIconContainer>
                      }
                    >
                      <Paragraph size='sm'>
                        This component is hidden by default in inquiry flows.
                      </Paragraph>
                    </Tooltip>
                  )}
                  {totalCountofRulesLogicAttrs > 0 && (
                    <Tooltip
                      side='top'
                      iconTrigger={
                        <ComponentLogicIconContainer
                          tabIndex={1}
                          aria-label={`Open logic tab for ${component.type} on ${stepName} screen`}
                          role='button'
                          id={`${component.name}-${stepName}-logic-panel-trigger`}
                          onClick={(e: MouseEvent<HTMLDivElement>) =>
                            onLogicAttributeTriggerClick(e, component)
                          }
                        >
                          <LogicIcon />
                        </ComponentLogicIconContainer>
                      }
                    >
                      <Paragraph size='sm'>Click to edit logic rules for this component.</Paragraph>
                    </Tooltip>
                  )}
                  <IconWrapper
                    tabIndex={1}
                    aria-label={`Delete ${component.type} on ${stepName} screen`}
                    role='button'
                    id={`${component.name}-${stepName}`}
                    onClick={onDeleteComponentButtonClick}
                  >
                    <TrashIcon />
                  </IconWrapper>
                </CenteredContainer>
              }
              {...(!componentSupportedInEditor && {
                prefixIcon: <CircleAlertIcon />,
              })}
            >
              {componentSupportedInEditor ? componentItem.displayName : component.type}
            </StyledEditorNavItem>
            {isUiParentComponentConfig(component) && (
              <ChildComponentsContainer>
                {buildComponentItems(stepName, component.config.children)}
              </ChildComponentsContainer>
            )}
          </div>
        );
      });
    },
    [
      selectedComponent,
      selectedStepConfig,
      handleComponentDelete,
      onLogicAttributeTriggerClick,
      setSelectedComponentAndStep,
    ],
  );

  return <>{buildComponentItems(name, components)}</>;
};

export default ScreenComponentList;
